import React, { useState, useEffect } from 'react';
import size from 'lodash/size';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useUniversalNavigationContext } from '@fff-web/fff-global-nav';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useUserContext } from '@fff-web/fff-utilities';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared/Toast';
import Toast from '@fff-web/fff-ui-shared/Toast';

import useScreenMode from 'hooks/useScreenMode';
import { VIEW_SIZE } from './utils/device';
import {
  setUserAvatar,
  getUserCredits,
  getRemainingBoxes,
  getUserNextRenewal,
  getAccount,
} from './services/api/account';
import {
  fetchUserAvatarRequest,
  fetchUserAvatarSuccess,
  receiveUserStatus,
} from './services/actions/userActions';
import { getBillingInfo } from './services/api/billing';
import MyAccountRoutes from './routes/MyAccountRoutes';
import Profile from './Profile/Profile';
import MyAccountNav from './MyAccountNav/MyAccountNav';
import Layout from '../../components/Layout';
import viewSizeTypes from '../../utils/viewSizeTypes';

const ANNUAL_SUBSCRIPTION = 'annual';
const EXPIRED_USER = 'expired';
const CLUB_MEMBER = 'club';

const MyAccountPage = ({
  firstName,
  lastName,
  activationDate,
  shippingAddress,
  subscription,
  subscriptionState,
  avatarUrl,
  updateAvatar,
  phone,
  email,
  getUserAvatar,
  updateAccount,
  fetchingUserStatus,
  userStatusError,
  userRole,
  planCode,
}) => {
  const ldClient = useLDClient();
  const { userInfo } = useUserContext();
  const { membershipInfo } = useUniversalNavigationContext();
  const { user } = userInfo;

  const { myAccountRemainingBoxes: myAccountRemainingBoxesFF } = useFlags();
  const isClubMember = userRole === CLUB_MEMBER;

  const screenMode = useScreenMode();
  const [credits, setCredits] = useState(null);
  const [billingInfo, setBillingInfo] = useState({});
  const [remainingBoxes, setRemainingBoxes] = useState([]);
  const [nextRenewal, setNextRenewal] = useState({});
  const [flagsReady, setFlagsReady] = useState(false);
  const viewSize = viewSizeTypes[screenMode];
  const isDesktop = viewSize === VIEW_SIZE.large || viewSize === VIEW_SIZE.xLarge;
  const displayRemainingBoxes =
    subscription === ANNUAL_SUBSCRIPTION && myAccountRemainingBoxesFF && !!size(remainingBoxes);

  useEffect(() => {
    const fetchData = async () => {
      const accountResponse = await getAccount();
      updateAccount(accountResponse);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCredits = async () => {
      const fetchedCredits = await getUserCredits();
      setCredits(fetchedCredits);
    };
    const fetchBillingInfo = async () => {
      const response = await getBillingInfo();
      setBillingInfo({ ...response, postalCode: response.zip });
    };
    const fetchUserNextRenewal = async () => {
      const response = await getUserNextRenewal();
      if (size(response)) setNextRenewal(response);
    };
    fetchCredits();
    fetchBillingInfo();
    if (!avatarUrl) getUserAvatar();
    if (subscriptionState !== EXPIRED_USER) fetchUserNextRenewal();
  }, []);

  useEffect(() => {
    const fetchRemainingBoxes = async () => {
      const items = await getRemainingBoxes();
      if (Array.isArray(items)) {
        setRemainingBoxes(items);
      }
    };
    if (subscription === ANNUAL_SUBSCRIPTION && !size(remainingBoxes)) {
      fetchRemainingBoxes();
    }
  }, [subscription]);

  useEffect(() => {
    ldClient.identify({ key: user?.userId }).then(() => setFlagsReady(true));
  });

  const uploadMyAvatar = async (formData) => {
    try {
      const { userAvatarUrl } = await setUserAvatar(formData);
      updateAvatar(userAvatarUrl);
      showSuccessToast(
        'Updated Profile Photo',
        'Your profile photo change has been successfully saved.',
      );
    } catch (err) {
      showErrorToast(
        'Profile Picture Upload Failed',
        'Your image did not meet our requirements. Please upload a PNG or JPG of at least 300x300 that is under 500KB.',
      );
    }
  };
  return (
    <Layout isLoading={userStatusError || fetchingUserStatus || !flagsReady}>
      <div className={`pandora ${viewSize}`} data-testid="general-container">
        <Profile
          isDesktop={isDesktop}
          firstName={firstName}
          lastName={lastName}
          activationDate={activationDate}
          subscription={subscription}
          subscriptionState={subscriptionState}
          avatarUrl={avatarUrl}
          uploadAvatar={uploadMyAvatar}
          remainingBoxes={remainingBoxes}
          displayRemainingBoxes={displayRemainingBoxes}
          nextRenewal={nextRenewal}
          isClubMember={isClubMember}
          membershipInfo={membershipInfo}
          planCode={planCode}
        />
        {isDesktop && <MyAccountNav />}
        <MyAccountRoutes
          firstName={firstName}
          lastName={lastName}
          shippingAddress={shippingAddress}
          phone={phone}
          email={email}
          credits={credits}
          billingInfo={billingInfo}
          setBillingInfo={setBillingInfo}
          nextRenewal={nextRenewal}
          isDesktop={isDesktop}
          isClubMember={isClubMember}
        />
        <Toast />
      </div>
    </Layout>
  );
};
const mapStateToProps = (state) => {
  const { userState, viewState } = state;
  return {
    viewSize: viewState.viewSize,
    firstName: userState.firstName,
    lastName: userState.lastName,
    activationDate: userState.activationDate,
    shippingAddress: userState.shippingAddress,
    subscriptionState: userState.subscriptionState,
    subscription: userState.subscription,
    userId: userState.userId,
    userRole: userState.role,
    avatarUrl: userState.userAvatarUrl,
    uploadingImageError: userState.error,
    phone: userState.phoneNumber,
    email: userState.email,
    fetchingUserStatus: userState.fetchingUserStatus,
    userStatusError: userState.userStatusError,
    planCode: userState.planCode,
  };
};

export default compose(
  connect(mapStateToProps, {
    getUserAvatar: fetchUserAvatarRequest,
    updateAvatar: fetchUserAvatarSuccess,
    updateAccount: receiveUserStatus,
  }),
)(MyAccountPage);
